import { NgFor, NgIf } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Optional,
	Output
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent, TypographyComponent } from 'components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReportChartDrawingComponent } from '../../widgets/chart-drawing/chart-drawing.component';
import { ReportChartTypeToolbarComponent } from '../../components/report-chart-type-toolbar/report-chart-type-toolbar.component';
import { ReportSlideSourceComponent } from '../../components/report-slide-source/report-slide-source.component';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import { ReportCreatorCardsComponent } from '../../components/report-creator-cards/report-creator-cards.component';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import { ReportQuoteCardComponent } from '../../components/report-quote-card/report-quote-card.component';
import { ReportAlertsTimelineComponent } from '../../components/report-alerts-timeline/report-alerts-timeline.component';
import { ReportReviewRatingsComponent } from '../../components/report-review-ratings/report-review-ratings.component';
import { ReportDateRange } from '../../../../models/preview/preview.models';
import {
	ReportBlockCreator,
	ReportBlockData,
	ReportBlockEntity,
	ReportBlockQuote,
	ReportBlockRender
} from '../../../../services/report-blocks.service';
import { ReportSharedReadsService } from '../../../../services/report-shared-reads.service';
import { ReportDataSource } from '../../../../services/report-settings.service';
import { ReportHome } from '../../../../services/report-home';
import { blockSource, ReportSource } from '../../../../services/report-source';
import { ReportLanguageService } from '../../../../services/report-language.service';
import { isCreatorsBlock } from '../../../../services/report-creators';
import { reportMessage } from '../../../../services/report-messages';
import {
	ReportInlineTextField,
	blockTitleField
} from '../../../../services/report-inline-edit.service';
import { blockSubjectNames } from '../../../../services/report-block-chart-config';
import { chartTypeAdviceFor } from '../../../../services/report-chart-types';
import {
	isQuoteBlock,
	isQuoteListBlock,
	ReportQuoteChange
} from '../../../../services/report-quote';

/**
 * What draws a block that has something to draw: the slide's own timeline or
 * rating bars — the two renders only library sections take, kept out of
 * `REPORT_BLOCK_RENDERS` — or the chart drawing, for every other render.
 */
type SlideDrawing = 'timeline' | 'rating_bars' | 'chart';

function slideDrawing(
	render: ReportBlockRender | null | undefined
): SlideDrawing {
	return render === 'timeline' || render === 'rating_bars' ? render : 'chart';
}

/**
 * A slide the reader asked for, rather than one of the sixteen.
 *
 * It renders whatever the backend resolved: a chart, a table or a row of
 * headline figures. Unlike its sixteen siblings it fetches nothing — the data
 * arrives already read, because a block's numbers come from the catalogue
 * resolver rather than from a service of its own. That is what lets one
 * component cover every block we will ever add, and what lets its type be
 * switched without asking for the numbers again: the drawing below redraws the
 * same block as whatever the reader picks.
 *
 * An empty block prints its reason. A chart with no bars and a chart that
 * failed look identical to a reader, and "nothing was collected from web
 * sources in this period" is the useful half of that answer. That includes a
 * period too thin to read (T-2384): the backend marks such a block empty and
 * says so in the note, and it is drawn in the same honest state as a thin
 * fixed section.
 *
 * Every block ends on its source line, like every fixed section: whose
 * numbers, what was counted, how, and for which window.
 *
 * A Creator shortlist is drawn as creator cards rather than a chart, one per
 * account (see `report-creators.ts`), and keeps its source line: it is a read
 * of the window like any chart. With nobody in the window it says so like any
 * empty block. In the editor a card whose creator Discovery knows asks the
 * page to open that profile (`creatorOpened`); nowhere else is it clickable.
 *
 * A block may be about SEVERAL entities. Nothing here counts series — the
 * chart config owns that — but the heading has to say who is being compared,
 * because the planner's title usually says only what was measured.
 *
 * A quote card is not a chart: one mention, drawn by its own card, which also
 * draws its own empty state so that in the editor a card whose mention is gone
 * still offers another. It has no source line — it counts nothing, and the
 * card names where it came from itself.
 *
 * A list of mentions is not a chart either: one quote card per post, in the
 * order the list ranks them, laid out as a grid so ten fit a PDF page. Unlike
 * a quote card it keeps its source line, because it IS a read of the period:
 * whose mentions, which window. Its authors are hidden with one switch for
 * the whole list, and in the editor each card offers Keep this one.
 */
@Component({
	standalone: true,
	selector: 'app-custom-block-slide',
	imports: [
		NgIf,
		NgFor,
		// matTooltip is silently inert without this.
		MatTooltipModule,
		IconComponent,
		TypographyComponent,
		ReportChartDrawingComponent,
		ReportCreatorCardsComponent,
		ReportChartTypeToolbarComponent,
		ReportSlideSourceComponent,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportQuoteCardComponent,
		ReportAlertsTimelineComponent,
		ReportReviewRatingsComponent
	],
	template: `
		<section class="block-slide" *ngIf="block">
			<typography variant="p/base_semibold" class="block-slide__title">
				<app-report-inline-text
					[text]="block.title"
					[field]="titleField"
					[editorMode]="editorMode"
				></app-report-inline-text>
			</typography>

			<typography
				variant="p/xs_regular"
				class="block-slide__entities"
				*ngIf="comparisonSubtitle"
			>
				{{ comparisonSubtitle }}
			</typography>

			<!-- Editor only: what the chart counts, and the type menu, over
			     the chart they change. The data control shows on an empty
			     chart too: a period too thin to read is fixed from there. -->
			<div class="block-slide__tools" *ngIf="editorMode">
				<button
					type="button"
					class="block-slide__data"
					aria-label="Change the data behind this chart"
					matTooltip="Period, filters, comparison and shape"
					matTooltipPosition="above"
					matTooltipClass="sentiment-tooltip"
					(click)="dataRequested.emit()"
				>
					<svg
						viewBox="0 0 16 16"
						width="16"
						height="16"
						aria-hidden="true"
					>
						<line x1="2.5" y1="4.5" x2="13.5" y2="4.5" />
						<line x1="2.5" y1="11.5" x2="13.5" y2="11.5" />
						<circle cx="10" cy="4.5" r="1.75" />
						<circle cx="6" cy="11.5" r="1.75" />
					</svg>
					Data
				</button>
				<!-- A list of mentions: one switch hides every card's author,
				     and the server leaves them out of the answer. -->
				<button
					*ngIf="quotes.length"
					type="button"
					class="block-slide__data"
					[attr.aria-pressed]="authorsHidden"
					[disabled]="busy"
					[matTooltip]="authorsTooltip"
					matTooltipPosition="above"
					matTooltipClass="sentiment-tooltip"
					(click)="toggleAuthors()"
				>
					<app-icon type="svg" [size]="14" color="currentColor">
						{{ authorsIcon }}
					</app-icon>
					<typography
						variant="p/xs_medium"
						class="block-slide__data-label"
					>
						{{ authorsLabel }}
					</typography>
				</button>
				<app-report-chart-type-toolbar
					*ngIf="renders.length > 1 && !block.empty"
					[types]="renders"
					[current]="block.render"
					[advice]="advice"
					(picked)="renderChange.emit($event)"
				></app-report-chart-type-toolbar>
			</div>

			<div
				class="block-slide__body"
				[class.block-slide__body--updating]="updating"
				[attr.aria-busy]="updating"
			>
				<app-report-quote-card
					*ngIf="isQuote; else list"
					[block]="block"
					[brandId]="brandId"
					[dateRange]="dateRange"
					[themeColor]="themeColor"
					[editorMode]="editorMode"
					[busy]="busy"
					(quoteChange)="quoteChange.emit($event)"
				></app-report-quote-card>
				<ng-template #list>
					<div
						class="block-slide__quotes"
						*ngIf="quotes.length; else chart"
					>
						<app-report-quote-card
							*ngFor="
								let quote of quotes;
								trackBy: trackByMention
							"
							class="block-slide__quote"
							[item]="quote"
							[themeColor]="themeColor"
							[editorMode]="editorMode"
							[busy]="busy"
							(keep)="keepMention.emit(quote.mention_id)"
						></app-report-quote-card>
					</div>
				</ng-template>
				<ng-template #chart>
					<app-slide-no-data
						*ngIf="block.empty; else cards"
						[message]="emptyText"
					></app-slide-no-data>
				</ng-template>
				<ng-template #drawing>
					<!-- The two drawings only a library section takes are the
					     slide's own; every other one is a chart or a table. -->
					<app-report-alerts-timeline
						*ngIf="drawnAs === 'timeline'"
						[block]="block"
						[themeColor]="themeColor"
					></app-report-alerts-timeline>
					<app-report-review-ratings
						*ngIf="drawnAs === 'rating_bars'"
						[block]="block"
						[themeColor]="themeColor"
					></app-report-review-ratings>
					<app-report-chart-drawing
						*ngIf="drawnAs === 'chart'"
						[block]="block"
						[themeColor]="themeColor"
						[source]="source"
					></app-report-chart-drawing>
				</ng-template>
				<!-- A Creator shortlist: cards rather than a chart. -->
				<ng-template #cards>
					<app-report-creator-cards
						*ngIf="creators.length; else drawing"
						[creators]="creators"
						[editorMode]="editorMode"
						(creatorOpened)="creatorOpened.emit($event)"
					></app-report-creator-cards>
				</ng-template>
			</div>

			<typography
				variant="p/xs_regular"
				class="block-slide__note"
				*ngIf="block.note && !block.empty"
			>
				{{ block.note }}
			</typography>

			<app-report-slide-source
				*ngIf="source"
				[source]="source"
			></app-report-slide-source>
		</section>
	`,
	styles: [
		`
			:host {
				display: block;
			}

			.block-slide {
				position: relative;
				display: flex;
				flex-direction: column;
				gap: 0.75rem;
				padding: 1.5rem;
			}

			/* The type menu shows while the chart is pointed at or focused. */
			.block-slide:hover,
			.block-slide:focus-within {
				--report-chart-types-opacity: 1;
				--report-chart-types-events: auto;
			}

			.block-slide__tools {
				position: absolute;
				/* Clear of the card's own remove button in the corner. */
				top: 0.75rem;
				right: 3rem;
				z-index: 2;
				display: flex;
				align-items: center;
				gap: 6px;
				/* Hidden, it must not swallow a click meant for the heading
				   under it; the controls opt back in when they show. */
				pointer-events: none;
			}

			/* Shown with the type menu, on the chart's hover or focus. */
			.block-slide__data {
				display: inline-flex;
				align-items: center;
				gap: 6px;
				height: 36px;
				padding: 0 12px 0 10px;
				border: 1px solid #d6e7ff;
				border-radius: 10px;
				background: #ffffff;
				box-shadow: 0 4px 14px rgba(35, 78, 143, 0.12);
				color: #6b7796;
				font: inherit;
				font-size: 0.8125rem;
				font-weight: 500;
				cursor: pointer;
				opacity: var(--report-chart-types-opacity, 0);
				pointer-events: var(--report-chart-types-events, none);
				transition: opacity 0.15s ease;
			}

			.block-slide__data:hover,
			.block-slide__data:focus-visible {
				color: #2872e1;
				outline: none;
				background: #f2f6fd;
			}

			.block-slide__data svg {
				fill: none;
				stroke: currentColor;
				stroke-width: 1.5;
				stroke-linecap: round;
			}

			.block-slide__data circle {
				fill: #ffffff;
			}

			/* The switch that hides a list's authors: pressed while they are
			   hidden, as the quote card's own switch is. Its opacity is the
			   reveal, so a busy page greys the colour, not the button. */
			.block-slide__data {
				&[aria-pressed='true'] {
					color: #2872e1;
					background: #e8f0fd;
				}

				&:disabled {
					color: #aab3c5;
					cursor: default;
				}

				.block-slide__data-label {
					color: inherit;
				}
			}

			/* A list of mentions: a card per post, as many to a row as fit.
			   Four fit across a PDF page, so ten take three rows. */
			.block-slide__quotes {
				display: grid;
				grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
				gap: 0.75rem;
				width: 100%;

				/* Each card shows its control on its own hover, not the
				   whole list's at once. */
				.block-slide__quote {
					--report-chart-types-opacity: 0;
					--report-chart-types-events: none;

					&:hover,
					&:focus-within {
						--report-chart-types-opacity: 1;
						--report-chart-types-events: auto;
					}
				}
			}

			/* A screen with no hover would otherwise never show it. */
			@media (hover: none) {
				.block-slide__data {
					opacity: 1;
					pointer-events: auto;
				}
			}

			/* The old numbers, while the chart is read again. */
			.block-slide__body--updating {
				opacity: 0.45;
				cursor: progress;
				transition: opacity 0.15s ease;
			}

			.block-slide__title {
				color: var(--report-value, #333a42);
			}

			.block-slide__entities {
				/* Sits directly under the title, so it reads as part of the
				   heading rather than as the first line of the block. */
				margin-top: -0.5rem;
				color: var(--report-value, #333a42);
				opacity: 0.6;
			}

			.block-slide__body {
				min-height: 12rem;
				display: flex;
				align-items: center;
			}

			.block-slide__note {
				opacity: 0.6;
			}
		`
	]
})
export class CustomBlockSlideComponent implements OnDestroy {
	/**
	 * What the source line needs that the block's data does not say: the
	 * report's brand (whose numbers a block without entities is), the window
	 * the block was resolved for, and — from its spec — who a block drawn for
	 * someone else, or for several, is about. Pages pass the same values the
	 * block was resolved with. Setters that touch only the source line, so a
	 * late brand name never redraws the chart.
	 */
	@Input() set brandId(value: string | null | undefined) {
		const brandId = value ?? '';
		if (brandId === this._brandId) {
			return;
		}
		this._brandId = brandId;
		this.brandLabel = null;
		this.brandChanged$.next();
		if (brandId) {
			let answeredAtOnce = true;
			this.sharedReads
				.brandLabel(brandId)
				.pipe(takeUntil(this.brandChanged$))
				.subscribe(label => {
					this.brandLabel = label;
					this.refreshSource();
					// The export page detaches its change detector after its
					// first pass, so a name that lands later reaches the PDF's
					// source line only if the block redraws itself.
					if (!answeredAtOnce) {
						this.cdr.detectChanges();
					}
				});
			answeredAtOnce = false;
		}
		this.refreshSource();
	}
	get brandId(): string {
		return this._brandId;
	}

	@Input() set dateRange(value: ReportDateRange | null | undefined) {
		this._dateRange = value ?? null;
		this.refreshSource();
	}
	get dateRange(): ReportDateRange | null {
		return this._dateRange;
	}

	/** The spec's `entities`, with their kinds — which the names in the
	 *  data (`entities` below) do not carry. */
	@Input() set specEntities(value: ReportBlockEntity[] | null | undefined) {
		this._specEntities = value ?? [];
		this.refreshSource();
	}

	@Input() set dataSource(value: ReportDataSource | null | undefined) {
		this._dataSource = value ?? null;
		this.refreshSource();
	}

	/**
	 * Where the report lives. On a report homed on a project or an industry, a
	 * block naming nobody is about that home, and its source line says so.
	 */
	@Input() set home(value: Pick<ReportHome, 'kind'> | null | undefined) {
		this.homeIsSource = !!value && value.kind !== 'brand';
		this.refreshSource();
	}

	// Handed straight to the drawing, which repaints on its own when the
	// report is, and not only when the block's data changes.
	@Input() themeColor: string | null = null;

	@Input() set block(value: ReportBlockData) {
		this._block = value;
		this.isQuote = isQuoteBlock(value);
		this.quotes = isQuoteListBlock(value) ? value.quotes ?? [] : [];
		this.authorsHidden = this.quotes.some(quote => quote.author_hidden);
		this.authorsIcon = this.authorsHidden ? 'eye_big' : 'eye_big_slash';
		this.authorsLabel = this.authorsHidden
			? 'Show authors'
			: 'Hide authors';
		this.authorsTooltip = this.authorsHidden
			? 'Show who wrote each mention again'
			: 'Hide the name, handle, picture and link to the post on every card';
		this.titleField = value ? blockTitleField(value) : null;
		this.entities = value ? blockSubjectNames(value) : [];
		this.comparisonSubtitle = this.buildComparisonSubtitle(value);
		this.renders = value?.renders ?? [];
		this.advice = chartTypeAdviceFor(value, this.renders);
		this.creators = isCreatorsBlock(value) ? value.creators ?? [] : [];
		this.emptyText =
			value?.note ||
			reportMessage(this.reportLanguage?.language, 'empty.nothing');
		this.drawnAs = slideDrawing(value?.render);
		this.refreshSource();
	}
	@Input() editorMode = false;
	/** The chart is being read again; its old numbers show, dimmed. */
	@Input() updating = false;
	/** The page is reading its blocks again; a quote card's controls wait. */
	@Input() busy = false;

	/** The reader picked another type for this chart. */
	@Output() renderChange = new EventEmitter<ReportBlockRender>();
	/** In the editor: the reader opened a Creator shortlist card's profile. */
	@Output() creatorOpened = new EventEmitter<ReportBlockCreator>();
	/** The reader asked to change what this chart counts. */
	@Output() dataRequested = new EventEmitter<void>();
	/**
	 * The reader swapped a quote card's mention, or hid or showed its author —
	 * or, on a list of mentions, every card's author at once.
	 */
	@Output() quoteChange = new EventEmitter<ReportQuoteChange>();
	/** Keep this one, on a card of a list: the post to keep, by its id. */
	@Output() keepMention = new EventEmitter<string>();

	get block(): ReportBlockData {
		return this._block;
	}

	private _block!: ReportBlockData;
	private _brandId = '';
	private _dateRange: ReportDateRange | null = null;
	private _specEntities: ReportBlockEntity[] = [];
	private _dataSource: ReportDataSource | null = null;
	private homeIsSource = false;
	private brandLabel: string | null = null;
	private brandChanged$ = new Subject<void>();

	/** Where this block's numbers came from; null until the block and its
	 *  window are both known. The drawing gives each stat card its own. */
	source: ReportSource | null = null;

	/**
	 * The heading, as the field the reader types into in the editor. Only the
	 * heading: every figure under it was counted, and is not the reader's to
	 * type over.
	 */
	titleField: ReportInlineTextField | null = null;

	/** Who the block is about, when it is about more than one thing. */
	entities: string[] = [];
	comparisonSubtitle: string | null = null;
	/** What this block may be switched to — the catalogue's own list. */
	renders: ReportBlockRender[] = [];
	advice: Partial<Record<ReportBlockRender, string>> = {};
	/** One mention rather than a chart (see `report-quote.ts`). */
	isQuote = false;
	/** A list of mentions' cards, in its order; empty for every other block. */
	quotes: ReportBlockQuote[] = [];
	/** The list's one author switch, as its cards were drawn. */
	authorsHidden = false;
	authorsIcon = 'eye_big_slash';
	authorsLabel = 'Hide authors';
	authorsTooltip = '';
	/** Which of the slide's drawings a drawn block takes. */
	drawnAs: SlideDrawing = 'chart';
	emptyText = '';
	/** A Creator shortlist's cards; empty for every other block. */
	creators: ReportBlockCreator[] = [];

	constructor(
		private sharedReads: ReportSharedReadsService,
		private cdr: ChangeDetectorRef,
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	ngOnDestroy(): void {
		this.brandChanged$.next();
		this.brandChanged$.complete();
	}

	/** Every card's author hidden, or shown again: the page reads the list
	 *  again, because the server is what leaves them out. */
	toggleAuthors(): void {
		if (this.busy) {
			return;
		}
		this.quoteChange.emit({ hide_author: !this.authorsHidden });
	}

	trackByMention(_index: number, quote: ReportBlockQuote): string {
		return quote.mention_id;
	}

	private refreshSource(): void {
		this.source = this.isQuote
			? null
			: blockSource(
					this._block,
					this._specEntities,
					this.brandLabel,
					this._dateRange,
					this._dataSource,
					this.reportLanguage?.language,
					this.homeIsSource
			  );
	}

	/**
	 * Who the block is about, printed under the title when it is about more
	 * than one.
	 *
	 * The heading is whatever the planner wrote, which is usually the
	 * catalogue's own name — "Mentions over time" says what was measured and
	 * not who it was measured for. On a chart the legend supplies the other
	 * half, but a table and a row of figures have no legend, so a comparison
	 * would otherwise reach the reader with two brands in it and nothing at the
	 * top saying so.
	 *
	 * Skipped when the title already names them all, so an AI-written "Costco
	 * vs Sam's Club" is not printed twice.
	 */
	private buildComparisonSubtitle(block: ReportBlockData): string | null {
		if (this.entities.length < 2) {
			return null;
		}
		const title = (block.title ?? '').toLowerCase();
		if (
			this.entities.every(entity => title.includes(entity.toLowerCase()))
		) {
			return null;
		}
		return this.entities.join(' vs ');
	}
}
